import { useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, AlertTriangle, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { Annotation } from '@/types/dora-annotations';

interface AnnotationCardProps {
  annotation: Annotation;
  onApply: () => void;
  onEdit: (editedText: string) => void;
}

export function AnnotationCard({ annotation, onApply, onEdit }: AnnotationCardProps) {
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [editingSuggestion, setEditingSuggestion] = useState(false);
  const [editedSuggestion, setEditedSuggestion] = useState('');

  // Filled blank annotation (simple green badge)
  if (annotation.type === 'filled_blank' && annotation.blank_filled) {
    return (
      <Alert className="mt-2 border-green-500 bg-green-50 dark:bg-green-950">
        <CheckCircle2 className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-sm">
          <strong>Auto-filled:</strong> {annotation.blank_filled.filled_value}
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            Source: {annotation.blank_filled.source}
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Semantic issue annotation (expandable with suggestion)
  if (annotation.type === 'semantic_issue' && annotation.semantic_issue) {
    const issue = annotation.semantic_issue;
    const severityColor: Record<string, string> = {
      'CRITICAL': 'border-red-500 bg-red-50 dark:bg-red-950',
      'MEDIUM': 'border-yellow-500 bg-yellow-50 dark:bg-yellow-950',
      'LOW': 'border-blue-500 bg-blue-50 dark:bg-blue-950'
    };
    const color = severityColor[annotation.severity || 'LOW'];

    return (
      <Alert className={`mt-2 ${color}`}>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          <div className="space-y-2">
            {/* Issue header */}
            <div className="flex items-start justify-between">
              <div>
                <Badge className={`${color} mb-1`}>
                  {annotation.severity}
                </Badge>
                <div className="text-sm font-semibold">
                  This differs from your standard
                </div>
              </div>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={() => setShowSuggestion(!showSuggestion)}
              >
                {showSuggestion ? 'Hide' : 'Show'} Details
              </Button>
            </div>

            {/* Expandable details */}
            {showSuggestion && (
              <div className="space-y-3 pt-2 border-t">
                {/* Comparison */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <div className="font-semibold text-red-700 dark:text-red-400">BUYER REQUIRES:</div>
                    <div className="mt-1">{issue.buyer_requirement}</div>
                  </div>
                  <div>
                    <div className="font-semibold text-green-700 dark:text-green-400">YOUR STANDARD:</div>
                    <div className="mt-1">{issue.your_standard}</div>
                  </div>
                </div>

                {/* Differences */}
                <div>
                  <div className="text-xs font-semibold mb-1">Specific Differences:</div>
                  <ul className="text-xs space-y-1">
                    {issue.differences.map((diff, i) => (
                      <li key={i} className="flex items-start gap-1">
                        <span>•</span>
                        <span>{diff}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Impact */}
                <Alert className="border-orange-500">
                  <TrendingUp className="h-3 w-3" />
                  <AlertDescription className="text-xs">
                    <strong>Impact:</strong> {issue.commercial_impact}
                  </AlertDescription>
                </Alert>

                {/* Suggestion */}
                <div className="bg-white dark:bg-gray-900 rounded border p-3">
                  <div className="text-xs font-semibold mb-2 flex items-center justify-between">
                    <span>💡 Recommended Counter-Proposal:</span>
                    {!editingSuggestion && (
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => {
                          setEditedSuggestion(issue.recommendation);
                          setEditingSuggestion(true);
                        }}
                      >
                        Edit
                      </Button>
                    )}
                  </div>

                  {editingSuggestion ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editedSuggestion}
                        onChange={(e) => setEditedSuggestion(e.target.value)}
                        className="text-xs min-h-[80px]"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => {
                          onEdit(editedSuggestion);
                          setEditingSuggestion(false);
                          toast.success('Applied edited suggestion');
                        }}>
                          Apply Edited Version
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => {
                          setEditingSuggestion(false);
                        }}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="text-xs italic bg-blue-50 dark:bg-blue-950 p-2 rounded mb-2">
                        "{issue.recommendation}"
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={onApply}>
                          Apply This Suggestion
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => {
                          navigator.clipboard.writeText(issue.recommendation);
                          toast.success('Copied to clipboard');
                        }}>
                          Copy Text
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  return null;
}
